import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { type ThreadItem } from '@/types/messages';
import { formatBerlinDateTime } from '@/lib/berlin-time';

const formatRelative = (timestamp?: number | null): string => {
  if (!timestamp) return '—';
  const diff = Date.now() - timestamp;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'Gerade eben';
  if (diff < hour) return `${Math.floor(diff / minute)} Min`;
  if (diff < day) return `${Math.floor(diff / hour)} Std`;
  return formatBerlinDateTime(timestamp);
};

const ThreadSkeleton: React.FC = () => (
  <li className='flex items-center justify-between gap-4 rounded-lg border border-dashed border-border/60 bg-muted/30 p-4' data-testid='skeleton'>
    <div className='flex-1 space-y-2'>
      <Skeleton className='h-4 w-40' />
      <Skeleton className='h-3 w-24' />
      <Skeleton className='h-3 w-32' />
    </div>
    <Skeleton className='h-7 w-20' />
  </li>
);

const SubstituteMessagesPage = () => {
  const [showUnread, setShowUnread] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const threads = useQuery(api.app.messages.queries.listMyThreads, {}) as ThreadItem[] | undefined;
  const markRead = useMutation(api.app.messages.mutations.markThreadRead);

  const normalizedSearch = React.useMemo(() => search.trim().toLowerCase(), [search]);
  const filtered = React.useMemo(() => {
    const list = threads ?? [];
    return list.filter((thread) => {
      const matchesUnread = !showUnread || !!thread.unread;
      const matchesSearch = !normalizedSearch ? true : (thread.duty?.title || '').toLowerCase().includes(normalizedSearch);
      return matchesUnread && matchesSearch;
    });
  }, [threads, showUnread, normalizedSearch]);

  const isLoading = threads === undefined;

  return (
    <div className='mx-auto w-full max-w-4xl space-y-6 p-6'>
      <div className='space-y-1'>
        <h1 className='text-3xl font-semibold tracking-tight'>Nachrichten</h1>
        <p className='max-w-3xl text-sm text-muted-foreground'>Behalten Sie Ihre Dienst-Kommunikation im Blick. Chats werden aktiv, sobald Sie einem Dienst zugewiesen sind.</p>
      </div>

      <Card>
        <CardHeader className='gap-4 sm:flex-row sm:items-end sm:justify-between'>
          <div className='space-y-1'>
            <CardTitle className='text-xl'>Unterhaltungen</CardTitle>
            <CardDescription>Sortiert nach letzter Aktivität, inklusive ungelesener Hinweise.</CardDescription>
          </div>
          <div className='flex flex-col-reverse gap-3 sm:flex-row sm:items-center'>
            <label className='inline-flex items-center gap-2 text-sm text-muted-foreground'>
              <input type='checkbox' checked={showUnread} onChange={(event) => setShowUnread(event.target.checked)} className='size-4 rounded border-border text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring' />
              Nur ungelesene
            </label>
            <div className='sm:w-64'>
              <Input placeholder='Diensttitel durchsuchen' value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ul className='space-y-3'>
            {isLoading ? (
              <>
                <ThreadSkeleton />
                <ThreadSkeleton />
                <ThreadSkeleton />
              </>
            ) : filtered.length === 0 ? (
              <li className='rounded-lg border border-dashed border-border bg-muted/20 p-6 text-sm text-muted-foreground'>Keine Unterhaltungen gefunden.</li>
            ) : (
              filtered.map((thread) => {
                const dutyId = thread.duty?._id;
                const linkProps = dutyId
                  ? {
                      to: '/substitute/$id' as const,
                      params: { id: String(dutyId) },
                    }
                  : null;
                return (
                  <li
                    key={thread.threadId}
                    data-testid='thread-item'
                    data-thread-id={thread.threadId}
                    data-duty-id={dutyId}
                    className={cn(
                      'group flex flex-col gap-3 rounded-lg border border-border bg-card p-4 transition hover:border-primary/60 hover:bg-accent/40 md:flex-row md:items-center md:justify-between',
                      thread.unread ? 'ring-1 ring-primary/40' : undefined
                    )}
                  >
                    <div className='space-y-1'>
                      <div className='flex items-center gap-2'>
                        {linkProps ? (
                          <Link {...linkProps} preload='intent' className='text-base font-medium text-foreground underline-offset-2 transition hover:text-primary hover:underline' data-testid='duty-title'>
                            {thread.duty?.title || 'Ohne Titel'}
                          </Link>
                        ) : (
                          <span className='text-base font-medium text-foreground' data-testid='duty-title'>
                            {thread.duty?.title || 'Ohne Titel'}
                          </span>
                        )}
                        {thread.unread ? (
                          <Badge variant='secondary' className='uppercase' data-testid='unread-badge'>
                            Neu
                          </Badge>
                        ) : null}
                      </div>
                      <div className='text-sm text-muted-foreground' data-testid='duty-location' data-timestamp={thread.lastMessage?.createdAt ?? undefined}>
                        <span data-testid='location-text'>{thread.duty?.city || '—'}</span> • <span data-testid='last-activity'>Letzte Aktivität {formatRelative(thread.lastMessage?.createdAt ?? undefined)}</span>
                      </div>
                      {thread.lastMessage?.content ? (
                        <div className='line-clamp-2 text-sm text-muted-foreground' data-testid='message-preview'>
                          {thread.lastMessage.content}
                        </div>
                      ) : null}
                    </div>
                    <div className='flex items-center gap-3'>
                      {linkProps ? (
                        <Link {...linkProps} preload='intent' className='text-sm font-medium text-primary underline-offset-4 transition hover:underline'>
                          Chat öffnen
                        </Link>
                      ) : (
                        <span className='text-sm text-muted-foreground'>Chat aktuell nicht verfügbar</span>
                      )}
                      {thread.unread ? (
                        <button type='button' className='text-sm text-muted-foreground underline-offset-4 transition hover:text-foreground hover:underline' onClick={() => markRead({ threadId: thread.threadId })}>
                          Als gelesen markieren
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/_protected/substitute/messages')({
  component: SubstituteMessagesPage,
});
